import { useSyncExternalStore } from 'react';

/**
 * Data saver mode.
 *
 * When on, the app stops loading (background) images and shows a neutral
 * placeholder in their place — see the `html.datasaver` rules in index.css and
 * the image components that read this hook. It is OFF by default, manual-only,
 * and intentionally not remembered across sessions: it lives in sessionStorage
 * so closing the tab clears it.
 *
 * The flag is mirrored onto a `datasaver` class on <html> so plain CSS can
 * neutralize the inline `background-image: url(...)` styles scattered across the
 * app without touching every call site.
 */
const KEY = 'dataSaver';
const EVENT = 'datasaverchange';

export function isDataSaverOn() {
  try {
    return sessionStorage.getItem(KEY) === '1';
  } catch {
    // Private mode / storage disabled — treat as off.
    return false;
  }
}

function applyClass(on) {
  try {
    document.documentElement.classList.toggle('datasaver', on);
  } catch {
    /* no document (SSR/tests) — nothing to do */
  }
}

/** Reflect the stored flag onto <html> as early as possible (called from index.js). */
export function initDataSaver() {
  applyClass(isDataSaverOn());
}

/** Manual toggle. Persists for the tab session and notifies subscribers. */
export function setDataSaver(on) {
  try {
    sessionStorage.setItem(KEY, on ? '1' : '0');
  } catch {
    /* ignore storage failures — the in-page class still updates below */
  }
  applyClass(on);
  window.dispatchEvent(new Event(EVENT));
}

function subscribe(callback) {
  window.addEventListener(EVENT, callback);
  // `storage` covers other tabs toggling it; harmless if it never fires.
  window.addEventListener('storage', callback);
  return () => {
    window.removeEventListener(EVENT, callback);
    window.removeEventListener('storage', callback);
  };
}

/** Reactive read of the data-saver flag. */
export default function useDataSaver() {
  return useSyncExternalStore(subscribe, isDataSaverOn, () => false);
}
